import { IPayableSchema, IWorkLog } from "@wingspanhq/payments/dist/interfaces";
import uniq from "lodash/uniq";
import { useMemo } from "react";
import { WSQueryCache, WSQueryConfig } from "@ws-react-query";
import { useWSQuery } from "../../../query/helpers";
import { QUERY_PAYABLE } from "../../../query/payments/keys";
import { paymentsService } from "../../../services/payments";
import { concurrentActions } from "../../../utils/serviceHelper";

const QUERY_PAYABLES_BY_IDS = "QUERY_PAYABLES_BY_IDS";

const useQueryPayablesByIds = (
  payableIds: string[],
  config?: WSQueryConfig<IPayableSchema[]>
) =>
  useWSQuery<IPayableSchema[]>(
    [QUERY_PAYABLES_BY_IDS, payableIds],
    async () => {
      const actions = payableIds.map(payableId => async () => {
        const cachedPayable = WSQueryCache.getQueryData<IPayableSchema>([
          QUERY_PAYABLE,
          payableId
        ]);

        if (cachedPayable) {
          return cachedPayable;
        }

        const payable = await paymentsService.payable.get(payableId);

        WSQueryCache.setQueryData([QUERY_PAYABLE, payableId], payable);

        return payable;
      });

      const allPayables = await concurrentActions(actions);

      return allPayables;
    },
    config
  );

export const usePayablesMap = (workLogs: IWorkLog[] | undefined) => {
  const payableIds = useMemo(
    () =>
      uniq(
        (workLogs || [])
          .map(workLog => workLog.invoiceId)
          .filter((id): id is string => !!id)
      ),
    [workLogs]
  );

  const queryPayables = useQueryPayablesByIds(payableIds, {
    enabled: payableIds.length > 0
  });

  const payablesMap = useMemo(() => {
    const map = new Map<string, IPayableSchema>();
    (queryPayables.data || []).forEach(payable => {
      map.set(payable.payableId, payable);
    });
    return map;
  }, [queryPayables.data]);

  return {
    payablesMap,
    isLoading: queryPayables.isLoading && payableIds.length > 0
  };
};
